import type { SharedProps } from '@bonniernews/dn-design-system-web/assets/types/shared-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'

import type { DsIcon } from '@bonniernews/dn-design-system-web/types-lib/ds-icon.d.ts'

import { ButtonStandardProps } from '@bonniernews/dn-design-system-web/components/button/button-types.ts'
import Button from '@bonniernews/dn-design-system-web/components/button/button.tsx'
import { Pictogram } from '@bonniernews/dn-design-system-web/components/pictogram/pictogram.tsx'

export interface EmptyStateProps extends SharedProps {
  /** Icon name to use in the pictogram */
  iconName?: DsIcon
  /** Title */
  title?: string
  /** Body text */
  body?: string
  /** Object with text, href (optional), classNames (optional) and attributes (optional) */
  brandButton?: ButtonStandardProps
  /** Object with text, href (optional), classNames (optional) and attributes (optional) */
  primaryButton?: ButtonStandardProps
  /** Fixed pixel value is used for typography to prevent scaling based on html font-size */
  forcePx?: boolean
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/commponents/empty-state](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/empty-state)
 * - Storybook: [EmptyState](https://designsystem.dn.se/?path=/docs/basic-emptystate--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/empty-state/empty-state.scss'`
 */
export const EmptyState = ({
  iconName,
  title,
  body,
  brandButton,
  primaryButton,
  classNames,
  attributes,
  forcePx,
}: EmptyStateProps) => {
  const componentClassName = 'ds-empty-state'
  const classes = formatClassString([componentClassName, forcePx && 'ds-force-px', classNames])
  const hasButtons = brandButton || primaryButton
  const hasText = title || body
  const hasIcon = iconName

  return (
    <div className={classes} {...attributes}>
      {hasIcon && (
        <Pictogram
          variant='brand'
          size='large'
          iconName={iconName}
          forcePx={forcePx}
          attributes={{ 'aria-hidden': 'true' }}
        />
      )}
      {hasText && (
        <div className={`${componentClassName}__text`}>
          {title && <h2>{title}</h2>}
          {body && <span className={`${componentClassName}__body`}>{body}</span>}
        </div>
      )}
      {hasButtons && (
        <div className={`${componentClassName}__button-wrapper`}>
          {brandButton && (
            <Button
              text={brandButton.text}
              size={'lg'}
              variant='primary'
              href={brandButton.href}
              classNames={brandButton.classNames}
              attributes={brandButton.attributes}
              forcePx={forcePx}
              fullWidth
            />
          )}
          {primaryButton && (
            <Button
              text={primaryButton.text}
              size={'lg'}
              variant='secondaryOutline'
              href={primaryButton.href}
              classNames={primaryButton.classNames}
              attributes={primaryButton.attributes}
              forcePx={forcePx}
              fullWidth
            />
          )}
        </div>
      )}
    </div>
  )
}
